import type { ModifiableElementType } from "schemas/generated/fileModifiers";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Tag } from "@replo/design-system/components/tag/Tag";
import {
  Link,
  LucideImagePlay,
  ShoppingBag,
  Sparkles,
  SquareDashed,
  Type,
  X,
  Zap,
} from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

interface ElementTypeTagProps {
  elementType?: ModifiableElementType;
  showRemoveOnHover?: boolean;
  onRemove?: () => void;
}

const ElementTypeTag: React.FC<ElementTypeTagProps> = ({
  elementType,
  showRemoveOnHover = false,
  onRemove,
}) => {
  if (!elementType) {
    return null;
  }

  const { IconComponent, label } = exhaustiveSwitch({
    type: elementType,
  })({
    text: () => ({ IconComponent: Type, label: "Text" }),
    container: () => ({ IconComponent: SquareDashed, label: "Container" }),
    image: () => ({ IconComponent: LucideImagePlay, label: "Media" }),
    video: () => ({ IconComponent: LucideImagePlay, label: "Media" }),
    "product-section": () => ({
      IconComponent: ShoppingBag,
      label: "Buy Box",
    }),
    button: () => ({ IconComponent: Zap, label: "Button" }),
    icon: () => ({ IconComponent: Sparkles, label: "Icon" }),
    unknown: () => ({ IconComponent: SquareDashed, label: "Unknown" }),
    link: () => ({ IconComponent: Link, label: "Link" }),
  });

  return (
    <div className="group">
      <Tag size="base" variant="info">
        {showRemoveOnHover && (
          <span className="hidden group-hover:inline-flex">
            <Button
              variant="tertiary"
              size="sm"
              icon
              aria-label="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.();
              }}
            >
              <X size={14} />
            </Button>
          </span>
        )}
        <IconComponent
          className={showRemoveOnHover ? "group-hover:hidden" : undefined}
        />
        {label}
      </Tag>
    </div>
  );
};

export default ElementTypeTag;
